import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { X, Save, Loader2, Plus, AlertTriangle } from "lucide-react";
import DateInput from "./ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";

/**
 * Formulario de receta agronómica (orden de aplicación).
 *
 * Props:
 *  - items: productos controlados [{producto_id, descripcion, unidad, cantidad, ficha}] (heredados de la venta)
 *  - cliente: {id, nombre, ruc} heredado de la venta
 *  - initial: receta existente (edición) — pisa los valores por defecto
 *  - onConfirm(payload): async — recibe el payload de receta; si lanza error el modal queda abierto
 *  - confirmLabel: texto del botón principal
 *  - puedeCrearIngeniero: permite alta rápida de ingeniero desde el modal
 */
export default function RecetaFormModal({ open, onClose, onConfirm, token, logoTipo, cliente, items = [], initial = null, confirmLabel = "Guardar receta", title = "Receta agronómica", puedeCrearIngeniero = false }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);
  const [ingenieros, setIngenieros] = useState([]);
  const [chacras, setChacras] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ ingeniero_id: "", chacra_id: "", chacra_nombre: "", superficie_ha: "", coordenadas: "", cultivo: "", objetivo: "", metodo_aplicacion: "", fecha_aplicacion: "", observaciones: "" });
  const [dosis, setDosis] = useState({});       // producto_id -> {dosis_ha, carencia_dias}
  const [nuevoIng, setNuevoIng] = useState(null);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const itemsBase = initial?.items?.length ? initial.items.map(i => ({ ...i, cantidad: i.cantidad_vendida ?? i.cantidad })) : items;

  const cargarIngenieros = () =>
    fetch(`${API}/admin/recetario/ingenieros?logo_tipo=${encodeURIComponent(logoTipo || "")}`, { headers })
      .then(r => r.ok ? r.json() : []).then(setIngenieros).catch(() => {});

  useEffect(() => {
    if (!open) return;
    cargarIngenieros();
    if (cliente?.id) {
      fetch(`${API}/admin/recetario/chacras?logo_tipo=${encodeURIComponent(logoTipo || "")}&cliente_id=${encodeURIComponent(cliente.id)}`, { headers })
        .then(r => r.ok ? r.json() : []).then(setChacras).catch(() => setChacras([]));
    } else setChacras([]);
    setF({
      ingeniero_id: initial?.ingeniero_id || "", chacra_id: initial?.chacra_id || "", chacra_nombre: initial?.chacra_nombre || "",
      superficie_ha: initial?.superficie_ha ?? "", coordenadas: initial?.coordenadas || "", cultivo: initial?.cultivo || "",
      objetivo: initial?.objetivo || "", metodo_aplicacion: initial?.metodo_aplicacion || "",
      fecha_aplicacion: initial?.fecha_aplicacion || "", observaciones: initial?.observaciones || "",
    });
    const d = {};
    itemsBase.forEach(it => {
      const fi = it.ficha || {};
      d[it.producto_id || it.descripcion] = {
        dosis_ha: it.dosis_ha ?? fi.dosis_recomendada ?? "",
        carencia_dias: it.carencia_dias ?? fi.carencia_dias ?? "",
      };
    });
    setDosis(d);
    setNuevoIng(null);
    // eslint-disable-next-line
  }, [open]);

  const pickChacra = (id) => {
    if (!id) { set("chacra_id", ""); return; }
    const c = chacras.find(x => x.id === id);
    if (c) setF(p => ({ ...p, chacra_id: c.id, chacra_nombre: c.nombre, superficie_ha: c.superficie_ha || p.superficie_ha, coordenadas: c.coordenadas || p.coordenadas }));
  };

  const sup = Number(f.superficie_ha) || 0;
  const filas = itemsBase.map(it => {
    const k = it.producto_id || it.descripcion;
    const d = Number(dosis[k]?.dosis_ha) || 0;
    const total = Math.round(sup * d * 1000) / 1000;
    const vendida = Number(it.cantidad) || 0;
    return { it, k, d, total, vendida, aviso: vendida && d && Math.abs(total - vendida) > 0.0005 ? (total > vendida ? "La receta prevé usar más de lo vendido" : "Se vendió más de lo que cubre la receta") : "" };
  });

  const crearIngeniero = async () => {
    if (!nuevoIng?.nombre?.trim() || !nuevoIng?.matricula?.trim()) { toast.error("Nombre y matrícula son obligatorios"); return; }
    const r = await fetch(`${API}/admin/recetario/ingenieros`, { method: "POST", headers, body: JSON.stringify({ ...nuevoIng, logo_tipo: logoTipo }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(d.detail || "No se pudo crear el ingeniero"); return; }
    await cargarIngenieros(); set("ingeniero_id", d.id); setNuevoIng(null);
  };

  const guardar = async () => {
    if (!f.ingeniero_id) return toast.error("Elegí el ingeniero agrónomo");
    if (!f.chacra_nombre.trim()) return toast.error("Indicá la chacra / área");
    if (!(sup > 0)) return toast.error("Indicá la superficie en hectáreas");
    if (!f.cultivo.trim()) return toast.error("Indicá el cultivo");
    for (const x of filas) if (!(x.d > 0)) return toast.error(`Falta la dosis por hectárea de «${x.it.descripcion}»`);
    const payload = {
      ...f, superficie_ha: sup,
      items: filas.map(x => ({
        producto_id: x.it.producto_id, descripcion: x.it.descripcion, unidad: x.it.unidad, cantidad_vendida: x.vendida,
        dosis_ha: x.d, unidad_dosis: x.it.ficha?.unidad_dosis || x.it.unidad_dosis || x.it.unidad,
        carencia_dias: dosis[x.k]?.carencia_dias === "" ? null : dosis[x.k]?.carencia_dias,
        ingrediente_activo: x.it.ficha?.ingrediente_activo || x.it.ingrediente_activo, clase_toxicologica: x.it.ficha?.clase_toxicologica || x.it.clase_toxicologica,
      })),
    };
    setSaving(true);
    try { await onConfirm(payload); } catch (e) { toast.error(e?.message || "No se pudo guardar la receta"); } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/80 z-[400] flex items-center justify-center p-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col text-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <p className="font-semibold">{title}</p>
            <p className="text-xs text-slate-400">Cliente: {cliente?.nombre || initial?.cliente_nombre || "—"}{cliente?.ruc ? ` · RUC ${cliente.ruc}` : ""}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto p-6 space-y-4">
          <div>
            <label className={LABEL}>Ingeniero agrónomo *</label>
            <div className="flex gap-2">
              <select className={INPUT} value={f.ingeniero_id} onChange={e => set("ingeniero_id", e.target.value)}>
                <option value="">— elegir —</option>
                {ingenieros.map(i => <option key={i.id} value={i.id}>{i.nombre} · Mat. {i.matricula}</option>)}
              </select>
              {puedeCrearIngeniero && <button type="button" onClick={() => setNuevoIng({ nombre: "", matricula: "" })} className="px-3 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:text-white" title="Nuevo ingeniero"><Plus className="w-4 h-4" /></button>}
            </div>
            {ingenieros.length === 0 && !nuevoIng && <p className="text-amber-400 text-xs mt-1">No hay ingenieros cargados{puedeCrearIngeniero ? ": usá el + para agregar uno" : ": pedí que carguen uno en Recetario → Ingenieros"}.</p>}
            {nuevoIng && (
              <div className="mt-2 flex flex-wrap gap-2 items-end bg-white/5 rounded-lg p-3">
                <div className="flex-1 min-w-[160px]"><label className={LABEL}>Nombre</label><input className={INPUT} value={nuevoIng.nombre} onChange={e => setNuevoIng(p => ({ ...p, nombre: e.target.value }))} /></div>
                <div className="w-40"><label className={LABEL}>Matrícula</label><input className={INPUT} value={nuevoIng.matricula} onChange={e => setNuevoIng(p => ({ ...p, matricula: e.target.value }))} /></div>
                <button type="button" onClick={crearIngeniero} className="px-3 py-2 text-sm bg-arandu-blue rounded-lg">Agregar</button>
                <button type="button" onClick={() => setNuevoIng(null)} className="px-2 py-2 text-slate-400"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Chacra del cliente</label>
              <select className={INPUT} value={f.chacra_id} onChange={e => pickChacra(e.target.value)}>
                <option value="">{chacras.length ? "— otra / escribir abajo —" : "— sin chacras guardadas —"}</option>
                {chacras.map(c => <option key={c.id} value={c.id}>{c.nombre}{c.superficie_ha ? ` (${c.superficie_ha} ha)` : ""}</option>)}
              </select>
            </div>
            <div><label className={LABEL}>Chacra / Área *</label><input className={INPUT} value={f.chacra_nombre} onChange={e => setF(p => ({ ...p, chacra_nombre: e.target.value, chacra_id: "" }))} placeholder="Ej: Lote Don Mario" /></div>
            <div><label className={LABEL}>Superficie (hectáreas) *</label><input type="number" min="0" step="any" className={INPUT} value={f.superficie_ha} onChange={e => set("superficie_ha", e.target.value)} /></div>
            <div><label className={LABEL}>Coordenadas</label><input className={INPUT} value={f.coordenadas} onChange={e => set("coordenadas", e.target.value)} placeholder="-25.30, -57.63" /></div>
            <div><label className={LABEL}>Cultivo *</label><input className={INPUT} value={f.cultivo} onChange={e => set("cultivo", e.target.value)} placeholder="Soja, maíz, trigo…" /></div>
            <div><label className={LABEL}>Plaga / maleza objetivo</label><input className={INPUT} value={f.objetivo} onChange={e => set("objetivo", e.target.value)} /></div>
            <div><label className={LABEL}>Método de aplicación</label><input className={INPUT} value={f.metodo_aplicacion} onChange={e => set("metodo_aplicacion", e.target.value)} placeholder="Terrestre, aéreo…" /></div>
            <div><label className={LABEL}>Fecha de aplicación prevista</label><DateInput className={INPUT} value={f.fecha_aplicacion} onChange={e => set("fecha_aplicacion", e.target.value)} /></div>
          </div>

          <div>
            <label className={LABEL}>Productos y dosis</label>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs text-slate-400"><tr>
                  <th className="text-left px-3 py-2">Producto</th><th className="px-2 py-2 w-28">Dosis / Ha *</th><th className="px-2 py-2 w-24">Carencia (días)</th><th className="text-right px-3 py-2 w-36">Total (Ha × dosis)</th>
                </tr></thead>
                <tbody>
                  {filas.map(x => (
                    <tr key={x.k} className="border-t border-white/5">
                      <td className="px-3 py-2">{x.it.descripcion}
                        {x.it.ficha?.ingrediente_activo && <span className="block text-[11px] text-slate-500">{x.it.ficha.ingrediente_activo}{x.it.ficha.clase_toxicologica ? ` · Clase ${x.it.ficha.clase_toxicologica}` : ""}</span>}
                        {x.vendida > 0 && <span className="block text-[11px] text-slate-500">Vendido: {x.vendida} {x.it.unidad}</span>}
                        {x.aviso && <span className="flex items-center gap-1 text-[11px] text-amber-400"><AlertTriangle className="w-3 h-3" />{x.aviso}</span>}
                      </td>
                      <td className="px-2 py-2"><input type="number" min="0" step="any" className={INPUT} value={dosis[x.k]?.dosis_ha ?? ""} onChange={e => setDosis(p => ({ ...p, [x.k]: { ...p[x.k], dosis_ha: e.target.value } }))} /></td>
                      <td className="px-2 py-2"><input type="number" min="0" className={INPUT} value={dosis[x.k]?.carencia_dias ?? ""} onChange={e => setDosis(p => ({ ...p, [x.k]: { ...p[x.k], carencia_dias: e.target.value } }))} /></td>
                      <td className="px-3 py-2 text-right font-semibold">{x.total ? x.total.toLocaleString("es-PY", { maximumFractionDigits: 3 }) : "—"} <span className="text-xs text-slate-500">{x.it.unidad}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div><label className={LABEL}>Observaciones</label><textarea rows={2} className={INPUT} value={f.observaciones} onChange={e => set("observaciones", e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancelar</button>
          <button onClick={guardar} disabled={saving} className="flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
